import { useMemo } from "react";

import { unitCategories, unitsJson } from "@corca/data";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { InlineNodeField } from "@/components/doc/right-sidebar/active-node-style/fields/InlineNodeField";
import { mapUnitToSetChildren } from "@/components/doc/right-sidebar/active-node-style/fields/unitUtils";
import { NodeStyleProvider } from "@/components/expression/utils/NodeStyleProvider";
import { Icon } from "@/components/icons/Icon";
import { CursorContextProvider } from "@/components/input/CursorContextProvider";
import { SuggestionsProvider } from "@/components/suggestions/SuggestionsProvider";
import { setNodeChildren } from "@/components/suggestions/utils";

import { UnitsDropdown } from "./UnitsDropdown";

import type { NodeWithUnit } from "./types";
import type { ItemWithUnits } from "./UnitsDropdown";

type Props = {
  node: NodeWithUnit;
  className?: string;
};

export const UnitField = observer(function UnitField({ node, className }: Props) {
  const t = useTranslations("sidebar.activeNode");
  const { doc, unitNode } = node;
  const items: ItemWithUnits[] = useMemo(() => {
    if (!unitNode?.unitType) {
      return [];
    }

    const { unitType } = unitNode;

    return unitsJson
      .filter((unit) => unitCategories(unit).includes(unitType))
      .map((unit) => {
        const unitWithType = { ...unit, unitType };

        return {
          title: unit.name ?? "Unit",
          units: [unitWithType],
          onClick: () => {
            doc.transact(() => {
              setNodeChildren(doc, node, mapUnitToSetChildren(unitWithType));
            }, "UnitField:unitTypeChange");
          },
        };
      });
  }, [doc, unitNode, node]);

  if (!unitNode?.inlineNode) {
    return null;
  }

  return (
    <CursorContextProvider id={`unit-field-${node.id}`} doc={doc} boundary={unitNode.inlineNode}>
      <SuggestionsProvider accept={["input", "operatorSymbol", "operator", "plainText"]}>
        <NodeStyleProvider text="unit" view="text">
          <InlineNodeField
            node={unitNode.inlineNode}
            className={className}
            label={t("fields.unit")}
            action={
              <UnitsDropdown
                toggleButtonLabel={t("unit.actions.moreUnits")}
                node={node}
                items={items}
                toggleButtonComponent={
                  <div className="flex items-center font-mono text-xxs uppercase tracking-tight text-gray-500">
                    <Icon name="ChevronDown" />
                  </div>
                }
              />
            }
          />
        </NodeStyleProvider>
      </SuggestionsProvider>
    </CursorContextProvider>
  );
});
